import DateFnsUtils from "@date-io/date-fns";
import { Environment } from "@lyfegen-platform/graphql-schema/models";
import { parseDate } from "@lyfegen-platform/utils";
import { AppBar, CssBaseline, Toolbar } from "@material-ui/core";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { useRecoilValue, useSetRecoilState } from "recoil";
import Banner from "../../components/Banner";
import CustomizedSnackbars from "../../components/SnackbarWarning";
import ErrorBoundary from "../../ErrorBoundary";
import { useMapThemeClasses } from "../../hooks/use-map-theme-styles";
import { useDataQuery } from "../../hooks/useDataModule";
import { breadCrumbsState, UserOrg, userOrganization } from "../../store/layout";
import BottomDrawer from "./BottomDrawer";
import Breadcrumb from "./Breadcrumb";
import layoutStyles from "./layout-styles";
import LayoutSnackbar from "./LayoutSnackbar";
import NavBar, { Module } from "./NavBar";
import { GET_USER_CONTEXT_DATA } from "./queries";
import { GetCurrentOrganization } from "./types/GetCurrentOrganization";
import LayoutToastMessage from "./LayoutToastMessage";

interface IProps {
	modules: Array<Module>;
	isUserGuest?: boolean;
	children?: React.ReactChild | React.ReactChildren | Array<React.ReactChild>;
	handleLogout: () => void;
	identityServerUrl: string;
	serverEnvironment: Environment;
}

class LocalizedUtils extends DateFnsUtils {
	override date(value?: any): Date {
		return value ? parseDate(value) : new Date();
	}
}

export default function Layout(props: IProps) {
	const { mainRoot, toolBar } = layoutStyles();
	const { layoutRoot, appBarBg, layoutContainer } = useMapThemeClasses();

	const setUserOrganization = useSetRecoilState(userOrganization);
	const bottomDrawer = useRecoilValue(breadCrumbsState);

	const handleSetUserOrganization = (response: GetCurrentOrganization) => {
		setUserOrganization(response.CurrentOrganization as unknown as UserOrg);
		const dataLayer = (window as any).dataLayer;
		if (dataLayer) {
			dataLayer.push({
				event: "gtm_status_update",
				GPTFeatureEnabled: response.CurrentOrganization.libraryGPTFeatureEnabled,
			});
		}
	};

	useDataQuery<GetCurrentOrganization>(GET_USER_CONTEXT_DATA, {
		onCompleted: handleSetUserOrganization,
	});

	return (
		<div className={layoutRoot}>
			<MuiPickersUtilsProvider utils={LocalizedUtils}>
				<CssBaseline />
				<AppBar position="sticky" className={`${appBarBg}`}>
					<Toolbar classes={{ regular: toolBar }}>
						<NavBar handleLogout={props.handleLogout} identityServerUrl={props.identityServerUrl} modules={props.modules} />
					</Toolbar>
				</AppBar>
				<Banner />
				{!props.isUserGuest && (
					<Breadcrumb
						fromProps={bottomDrawer?.fromProps || false}
						links={bottomDrawer?.fromProps ? (bottomDrawer.links as any) : null}
						disableSticky={bottomDrawer?.disableSticky ?? false}
					/>
				)}
				<div className={layoutContainer}>
					<ErrorBoundary serverEnvironment={props.serverEnvironment}>
						<main className={mainRoot}>
							{(!bottomDrawer || !bottomDrawer?.links.length) && (
								<div
									style={{
										backgroundColor: "rgba(251,248,245,1)",
										height: 24,
										position: "sticky",
										top: 76,
										zIndex: 5,
										margin: "0px -5px",
										flexGrow: 0,
									}}
								/>
							)}
							<div style={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>{props.children}</div>
							<LayoutSnackbar />
							<BottomDrawer />
							<LayoutToastMessage />
						</main>
					</ErrorBoundary>
					<CustomizedSnackbars />
				</div>
			</MuiPickersUtilsProvider>
		</div>
	);
}
